/**
 * VisualizerBackground — the ONE shared visualizer background for every editor.
 * Reads the shared vizStore (project / enabled / reactTo / camera) and renders
 * the loaded .visualizer scene behind the editor. Each editor passes only what
 * differs: its playhead time, its audio analyser (for "React to: Audio"), and a
 * note-energy probe (for "React to: Notes").
 */

import React, { useMemo } from 'react';
import VizProjectHost from './VizProjectHost';
import ParticleBackground from './ParticleBackground';
import { useVizStore } from './vizStore';
import type { AudioFrame } from './types';

interface Props {
  /** Current song time in seconds (the editor playhead). */
  getTime: () => number;
  /** Live analyser tapping the playing song (for React-to: Audio). */
  getAnalyser?: () => AnalyserNode | null;
  audioContext?: AudioContext | null;
  /** 0..1 energy from chart notes near the playhead (for React-to: Notes). */
  getNoteEnergy?: () => number;
  style?: React.CSSProperties;
}

export default function VisualizerBackground({ getTime, getAnalyser, audioContext, getNoteEnergy, style }: Props) {
  const enabled = useVizStore(s => s.enabled);
  const project = useVizStore(s => s.project);
  const reactTo = useVizStore(s => s.reactTo);
  const cameraMode = useVizStore(s => s.cameraMode);

  // A single reusable synthetic frame for note-driven reactivity.
  const frame = useMemo<AudioFrame>(() => ({
    frequencies: new Float32Array(1024),
    waveform: new Float32Array(2048),
    bass: 0, mid: 0, treble: 0, level: 0, beat: false, beatIntensity: 0, bpm: 120,
  }), []);

  const sceneOn = enabled && !!project;

  const getAudioFrame = reactTo === 'notes'
    ? () => {
        const e = Math.max(0, Math.min(1, getNoteEnergy?.() ?? 0));
        frame.bass = e; frame.mid = e * 0.85; frame.treble = e * 0.6; frame.level = e;
        frame.beat = e > 0.6; frame.beatIntensity = e;
        // Cheap spectrum fill so scenes that sample the raw bins still respond.
        const fr = frame.frequencies;
        for (let i = 0; i < fr.length; i++) fr[i] = e * (1 - i / fr.length);
        return frame;
      }
    : undefined;

  return (
    <>
      {/* Particle layer (independent of a loaded scene; gated by the theme's
          bgVisual style). */}
      <ParticleBackground getAnalyser={getAnalyser} getNoteEnergy={getNoteEnergy} />

      {/* Loaded .visualizer scene engine. */}
      {sceneOn && project && (
        <VizProjectHost
          project={project.project}
          getTime={getTime}
          getAnalyser={reactTo === 'audio' ? getAnalyser : undefined}
          audioContext={reactTo === 'audio' ? audioContext : undefined}
          getAudioFrame={getAudioFrame}
          getCamera={() => {
            const s = useVizStore.getState();
            return s.cameraMode === 'manual' ? { x: s.camX / 100, y: s.camY / 100, zoom: s.camZ } : null;
          }}
          style={{
            position: 'absolute', inset: 0, width: '100%', height: '100%',
            transform: cameraMode === 'manual' ? undefined : 'translateY(-14%) scale(1.3)',
            transformOrigin: 'center center',
            ...style,
          }}
        />
      )}
    </>
  );
}
